// React Imports
import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";

// External Libraries
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";

// Local Components
import { LoginLayout } from "../LoginLayout/LoginLayout";

// Styles
import S from "./VerifySignupPage.module.scss";

// Helpers
import {
  confirmCognitoSignup,
  cognitoResendConfirmationCode,
} from "sj-ui-toolkit/src/integrations/cognito/cognito";

// Constants
import { SIGNUP_EMAIL_KEY } from "../../../constants/signup";
import { LoginAppRoutes } from "sj-ui-toolkit/src/constants/routes";

export default function VerifySignupPage() {
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current !== null) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailFromQuery = queryParams.get("email");
  const emailFromStorage = localStorage.getItem(SIGNUP_EMAIL_KEY);
  const email = emailFromQuery || emailFromStorage;

  useEffect(() => {
    if (emailFromQuery) {
      localStorage.setItem(SIGNUP_EMAIL_KEY, emailFromQuery);
    }
  }, [emailFromQuery]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsVerifying(true);

    if (!email) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Email is missing. Please try again.",
      });
      setIsVerifying(false);
      return;
    }

    try {
      const message = await confirmCognitoSignup({ email, code: verificationCode });
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: message,
      });

      if (emailFromStorage) {
        localStorage.removeItem(SIGNUP_EMAIL_KEY);
      }

      redirectTimeoutRef.current = setTimeout(() => {
        redirectTimeoutRef.current = null;
        setIsVerifying(false);
        navigate(LoginAppRoutes.route);
      }, 2000);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : String(error),
      });
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setShowEmailDialog(true);
      return;
    }
    await resendCodeWithEmail(email);
  };

  const resendCodeWithEmail = async (emailToUse: string) => {
    setIsResendingCode(true);

    try {
      const message = await cognitoResendConfirmationCode({
        email: emailToUse,
        metadata: { signUpFrom: "SJ_FE_MONOREPO" },
      });
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: message,
      });
      // Store the email for future use
      localStorage.setItem(SIGNUP_EMAIL_KEY, emailToUse);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsResendingCode(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!emailInput) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please enter your email address.",
      });
      return;
    }
    setShowEmailDialog(false);
    await resendCodeWithEmail(emailInput);
    setEmailInput("");
  };

  const getVerificationMessage = () => {
    if (emailFromQuery) {
      return "Thanks for signing up! Check your inbox to retrieve your verification code.";
    }
    if (emailFromStorage && !emailFromQuery) {
      return "Check your inbox to retrieve your verification code or request a new one below.";
    }
    return null;
  };

  return (
    <LoginLayout title={"Verify Your Account"}>
      <Toast ref={toast} />
      <Dialog
        header="Enter Your Email"
        visible={showEmailDialog}
        data-testid="verify-signup-email-dialog"
        onHide={() => {
          setShowEmailDialog(false);
          setEmailInput("");
        }}
        style={{ width: "450px" }}
        modal
        draggable={false}
      >
        <div className="flex flex-col gap-4">
          <p>Please enter your email address to resend the verification code.</p>
          <InputText
            id="email"
            name="email"
            data-testid="verify-signup-email-input"
            type="email"
            placeholder="Email Address"
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleEmailSubmit();
              }
            }}
            autoFocus
            required
            aria-label="Email address for resending verification code"
          />
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              data-testid="verify-signup-email-cancel-button"
              outlined
              severity="secondary"
              onClick={() => {
                setShowEmailDialog(false);
                setEmailInput("");
              }}
            />
            <Button
              label="Submit"
              data-testid="verify-signup-email-submit-button"
              onClick={handleEmailSubmit}
              loading={isResendingCode}
              disabled={isResendingCode}
            />
          </div>
        </div>
      </Dialog>
      <form className={S.root} onSubmit={handleSubmit} data-testid="verify-signup-form">
        <div className="mb-4" role="status" aria-live="polite">
          {getVerificationMessage() && <span>{getVerificationMessage()}</span>}
        </div>
        <div className="mt-5 flex flex-col gap-3">
          <InputText
            className="w-70"
            id="verificationCode"
            name="verificationCode"
            data-testid="verify-signup-code-input"
            placeholder="Verification Code"
            value={verificationCode}
            onChange={handleChange}
            required
            aria-label="Verification Code"
          />
          <Button
            className="w-70"
            type="submit"
            data-testid="verify-signup-submit-button"
            label="Next"
            loading={isVerifying}
            disabled={isVerifying}
          />
          <Button
            className="w-70"
            type="button"
            data-testid="verify-signup-resend-button"
            label="Resend Verification Code"
            severity="secondary"
            onClick={handleResendCode}
            loading={isResendingCode}
            disabled={isResendingCode}
          />
        </div>
      </form>
    </LoginLayout>
  );
}
